import * as React from "react";
import { makeStyles, Text, Button } from "@fluentui/react-components";
import { Copy16Regular } from "@fluentui/react-icons";
import CardAccent from "../shared/CardAccent";
import { copyToClipboard } from "../../../services/outlookActions";

const useStyles = makeStyles({
  card: {
    backgroundColor: "var(--bg-elevated, #FFFFFF)",
    border: "1px solid var(--border-primary, #E0E2E6)",
    borderRadius: "12px",
    boxShadow: "var(--shadow-sm, 0 1px 2px rgba(11,17,26,0.06))",
    overflow: "hidden",
    transition: "box-shadow 0.2s ease, transform 0.2s ease",
    ":hover": {
      boxShadow: "var(--shadow-md, 0 4px 8px rgba(11,17,26,0.08))",
      transform: "translateY(-1px)",
    },
  },
  content: {
    padding: "16px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "12px",
  },
  title: {
    fontSize: "14px",
    fontWeight: "600",
    color: "var(--text-primary, #0B111A)",
  },
  languagePill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 10px",
    borderRadius: "9999px",
    backgroundColor: "var(--color-info-bg, #EDF7FC)",
    color: "var(--color-info, #13A3E0)",
    fontSize: "11px",
    fontWeight: "600",
    lineHeight: "1.4",
    whiteSpace: "nowrap",
    textTransform: "capitalize",
  },
  body: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  paragraph: {
    fontSize: "13px",
    lineHeight: "1.55",
    color: "var(--text-secondary, #4A4F57)",
    margin: "0",
  },
  actions: {
    display: "flex",
    gap: "8px",
    marginTop: "14px",
    paddingTop: "12px",
    borderTop: "1px solid var(--border-primary, #E0E2E6)",
  },
  copyBtn: {
    minWidth: "auto",
    fontSize: "12px",
  },
  copiedText: {
    fontSize: "11px",
    color: "var(--color-success, #7ED956)",
    alignSelf: "center",
    marginLeft: "4px",
  },
});

/**
 * Safely extract plain text from a card field that might be a JSON string.
 * n8n sometimes returns the entire card object stringified as body_text,
 * and the string may be truncated (invalid JSON). Uses regex fallback.
 */
function extractText(raw) {
  if (!raw || typeof raw !== 'string') return '';
  const trimmed = raw.trim();
  // If it doesn't look like JSON, return as-is
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return raw;

  // Try valid JSON parse first
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed.body_text || parsed.body || parsed.body_markdown || parsed.text || raw;
    }
  } catch {
    // JSON.parse failed — likely truncated. Use regex extraction.
  }

  // Regex fallback: extract "body_text":"<value>" from the (possibly truncated) JSON string
  for (const field of ['body_text', 'body', 'body_markdown']) {
    const pattern = new RegExp(`"${field}"\\s*:\\s*"((?:[^"\\\\]|\\\\.)*)`, 's');
    const match = trimmed.match(pattern);
    if (match && match[1]) {
      try {
        const unescaped = JSON.parse('"' + match[1] + '"');
        if (unescaped && unescaped.length > 20 && !unescaped.trim().startsWith('{')) {
          return unescaped;
        }
      } catch {
        const manual = match[1]
          .replace(/\\n/g, '\n')
          .replace(/\\t/g, '\t')
          .replace(/\\"/g, '"')
          .replace(/\\\\/g, '\\');
        if (manual.length > 20 && !manual.trim().startsWith('{')) {
          return manual;
        }
      }
    }
  }

  return raw;
}

/**
 * TranslationCard — translated text with a language pill badge.
 *
 * @param {{ card: { language?: string, body_text?: string, body?: string, title?: string } }} props
 */
const TranslationCard = ({ card }) => {
  const styles = useStyles();
  const [copied, setCopied] = React.useState(false);

  // Safely extract text content, handling cases where n8n returns JSON as body_text
  const textContent = extractText(card.body_text || card.body || "");

  const handleCopy = async () => {
    const success = await copyToClipboard(textContent);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const paragraphs = textContent
    .split("\n")
    .filter((line) => line.trim());

  return (
    <div className={styles.card}>
      <CardAccent />
      <div className={styles.content}>
        <div className={styles.header}>
          <Text className={styles.title}>{card.title || "Translation"}</Text>
          {card.language && (
            <span className={styles.languagePill}>{card.language}</span>
          )}
        </div>

        <div className={styles.body}>
          {paragraphs.map((line, idx) => (
            <p key={idx} className={styles.paragraph}>
              {line}
            </p>
          ))}
        </div>

        <div className={styles.actions}>
          <Button
            className={styles.copyBtn}
            appearance="subtle"
            size="small"
            icon={<Copy16Regular />}
            onClick={handleCopy}
          >
            {copied ? "Copied!" : "Copy"}
          </Button>
          {copied && <Text className={styles.copiedText}>✓</Text>}
        </div>
      </div>
    </div>
  );
};

export default TranslationCard;
